import { logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import { analytics } from '../firebase/config';
import { track } from './telemetryService';

/**
 * Analytics Service for tracking user activities across the site. Key funnel events ALSO dual-write to our own
 * queryable `telemetry_events` spine via track(...) (for the admin Engagement view) — done before the GA4
 * guard, so our copy is captured even when Firebase Analytics is unavailable.
 */

// User Events
/** `google_one_tap` is tracked apart from `google` so the One Tap funnel can be read on its own — shipping a
 *  sign-in change we cannot measure is how a regression hides. */
export const trackLogin = (method: 'google' | 'email' | 'google_one_tap') => {
  track('login', { method });
  if (!analytics) return;
  logEvent(analytics, 'login', { method });
};

export const trackSignUp = (method: 'google' | 'email' | 'google_one_tap') => {
  track('sign_up', { method });
  if (!analytics) return;
  logEvent(analytics, 'sign_up', { method });
};

export const trackLogout = () => {
  if (!analytics) return;
  logEvent(analytics, 'logout');
};

export const setAnalyticsUser = (userId: string, email?: string, name?: string) => {
  if (!analytics) return;
  setUserId(analytics, userId);
  if (email || name) {
    setUserProperties(analytics, {
      email: email || undefined,
      name: name || undefined,
    });
  }
};

// Chat-first activation / engagement Events (the core product funnel:
// sign_up → conversation_started → agent_message_sent → retention_nudge_*)
export const trackConversationStarted = (conversationId: string, memberCount: number) => {
  track('conversation_started', { member_count: memberCount });
  if (!analytics) return;
  logEvent(analytics, 'conversation_started', { conversation_id: conversationId, member_count: memberCount });
};

export const trackAgentMessageSent = (conversationId: string, opts: { attachments?: number; targeted?: boolean } = {}) => {
  if (!analytics) return;
  logEvent(analytics, 'agent_message_sent', {
    conversation_id: conversationId,
    attachments: opts.attachments || 0,
    targeted: !!opts.targeted,
  });
};

// SYNTHESIZE step (analyst workflow) — the Decision pane. The successful brief IS the ACTIVATION moment
// ("first thesis pressure-tested"): GA4 identifies the first occurrence per user, so we just fire it each time.
// The intent CTA (opening the pane) is a funnel step before it. PII-free (ids + counts only).
export const trackDecisionPaneOpened = (conversationId: string) => {
  if (!analytics) return;
  logEvent(analytics, 'decision_pane_opened', { conversation_id: conversationId });
};

export const trackDecisionBriefGenerated = (
  conversationId: string,
  counts: { messages: number; sections: number; items: number },
) => {
  track('decision_brief_generated', { items: counts.items, message_count: counts.messages });
  if (!analytics) return;
  logEvent(analytics, 'decision_brief_generated', {
    conversation_id: conversationId,
    message_count: counts.messages,
    sections: counts.sections,
    // total items surfaced = a proxy for how much the multi-lens synthesis produced.
    items: counts.items,
  });
};

// ── MONETIZATION funnel (GA4 STANDARD commerce events, so GA4's built-in revenue reports work) ──
// The "measure → monetize" half: begin_checkout when a user starts a Stripe Checkout, purchase when they return
// having completed it. `value` is REAL money (USD, the pack/plan price); `credits` (WP) is just metadata.
// PII-free — ids + amounts only, no card data (Stripe handles payment; entitlement is granted by the webhook).
type PurchaseKind = 'topup' | 'subscription';

/** User started a Stripe Checkout (before the redirect). Pairs with trackPurchase to measure checkout→buy. */
export const trackCheckoutStarted = (kind: PurchaseKind, id: string, valueUsd?: number) => {
  track('begin_checkout', { kind, ...(valueUsd ? { value_usd: valueUsd } : {}) });
  if (!analytics) return;
  logEvent(analytics, 'begin_checkout', {
    currency: 'USD',
    ...(valueUsd ? { value: valueUsd } : {}),
    items: [{ item_id: id, item_name: id, item_category: kind, ...(valueUsd ? { price: valueUsd } : {}), quantity: 1 }],
  });
};

/** User returned from a COMPLETED Stripe Checkout (top-up or subscription). Fire ONCE per return. */
export const trackPurchase = (kind: PurchaseKind, id: string, valueUsd: number, credits?: number) => {
  track('purchase', { kind, value_usd: valueUsd, ...(credits ? { credits } : {}) });
  if (!analytics) return;
  logEvent(analytics, 'purchase', {
    transaction_id: `${kind}_${id}_${Date.now()}`,
    currency: 'USD',
    value: valueUsd,
    items: [{ item_id: id, item_name: id, item_category: kind, price: valueUsd, quantity: 1 }],
    ...(credits ? { credits } : {}),
  });
};

// ── DEVICE FIT (RF3-DEVICE: desktop is the workshop, mobile is the viewer) ──
// The landing stays fully browsable on a phone; the wall only appears when a visitor on a handheld tries to
// SIGN IN. These two answer the only questions that matter about it: how much of our arriving traffic is on a
// device the creation workflow does not fit, and how many of them push past the warning anyway. `source`
// names the surface that raised it (e.g. 'landing'), never anything about the person.
export const trackDesktopWallShown = (source: string) => {
  track('desktop_wall_shown', { source });
  if (!analytics) return;
  logEvent(analytics, 'desktop_wall_shown', { source });
};

export const trackDesktopWallBypassed = (source: string) => {
  track('desktop_wall_bypassed', { source });
  if (!analytics) return;
  logEvent(analytics, 'desktop_wall_bypassed', { source });
};

// Trial -> starter pack funnel (TRY-4, owner 2026-09-27: no free credits; the free first storyboard, then the
// starter pack). Dual-written like the other funnel events so the admin Engagement view can read the conversion.
// `surface` names WHERE the offer appeared, never anything about the person.
export type StarterPackSurface = 'trial_bring_to_life' | 'topup_card' | 'topup_notice' | 'wallet';

export const trackTrialOfferShown = (where: 'home' | 'episode', state: string) => {
  track('trial_offer_shown', { where, state });
  if (!analytics) return;
  logEvent(analytics, 'trial_offer_shown', { where, state });
};

export const trackStarterPackOfferShown = (surface: StarterPackSurface) => {
  track('starter_pack_offer_shown', { surface });
  if (!analytics) return;
  logEvent(analytics, 'starter_pack_offer_shown', { surface });
};

export const trackStarterPackOfferClicked = (surface: StarterPackSurface) => {
  track('starter_pack_offer_clicked', { surface });
  if (!analytics) return;
  logEvent(analytics, 'starter_pack_offer_clicked', { surface });
};

// Retention / monetization Events
export const trackRetentionNudgeShown = (chatId: string, messageCount: number) => {
  if (!analytics) return;
  logEvent(analytics, 'retention_nudge_shown', { chat_id: chatId, message_count: messageCount });
};

export const trackRetentionNudgeDismissed = (chatId: string) => {
  if (!analytics) return;
  logEvent(analytics, 'retention_nudge_dismissed', { chat_id: chatId });
};

export const trackRetentionNudgeClicked = (chatId: string) => {
  if (!analytics) return;
  logEvent(analytics, 'retention_nudge_clicked', { chat_id: chatId });
};

// Project Events
export const trackProjectCreated = (projectId: string, projectType?: string) => {
  if (!analytics) return;
  logEvent(analytics, 'project_created', {
    project_id: projectId,
    project_type: projectType || 'software',
  });
};

export const trackProjectViewed = (projectId: string) => {
  if (!analytics) return;
  logEvent(analytics, 'project_viewed', {
    project_id: projectId,
  });
};

export const trackProjectUpdated = (projectId: string) => {
  if (!analytics) return;
  logEvent(analytics, 'project_updated', {
    project_id: projectId,
  });
};

export const trackProjectDeleted = (projectId: string) => {
  if (!analytics) return;
  logEvent(analytics, 'project_deleted', {
    project_id: projectId,
  });
};

// Task Events
export const trackTaskCreated = (taskId: string, taskType: string, priority: string) => {
  if (!analytics) return;
  logEvent(analytics, 'task_created', {
    task_id: taskId,
    task_type: taskType,
    priority,
  });
};

export const trackTaskUpdated = (taskId: string, field?: string) => {
  if (!analytics) return;
  logEvent(analytics, 'task_updated', {
    task_id: taskId,
    field: field || 'general',
  });
};

export const trackTaskStatusChanged = (taskId: string, oldStatus: string, newStatus: string) => {
  if (!analytics) return;
  logEvent(analytics, 'task_status_changed', {
    task_id: taskId,
    old_status: oldStatus,
    new_status: newStatus,
  });
};

export const trackTaskDeleted = (taskId: string) => {
  if (!analytics) return;
  logEvent(analytics, 'task_deleted', {
    task_id: taskId,
  });
};

export const trackTaskCommented = (taskId: string) => {
  if (!analytics) return;
  logEvent(analytics, 'task_commented', {
    task_id: taskId,
  });
};

// Document Events
export const trackDocumentCreated = (documentId: string, documentType: string) => {
  if (!analytics) return;
  logEvent(analytics, 'document_created', {
    document_id: documentId,
    document_type: documentType,
  });
};

export const trackDocumentViewed = (documentId: string) => {
  if (!analytics) return;
  logEvent(analytics, 'document_viewed', {
    document_id: documentId,
  });
};

export const trackDocumentUpdated = (documentId: string) => {
  if (!analytics) return;
  logEvent(analytics, 'document_updated', {
    document_id: documentId,
  });
};

export const trackDocumentDeleted = (documentId: string) => {
  if (!analytics) return;
  logEvent(analytics, 'document_deleted', {
    document_id: documentId,
  });
};

// Diagram Events
export const trackDiagramCreated = (documentId: string) => {
  if (!analytics) return;
  logEvent(analytics, 'diagram_created', {
    document_id: documentId,
  });
};

export const trackDiagramExported = (documentId: string, format: 'pdf' | 'svg' | 'json') => {
  if (!analytics) return;
  logEvent(analytics, 'diagram_exported', {
    document_id: documentId,
    format,
  });
};

// Team Events
export const trackTeamMemberInvited = (projectId: string, role: string) => {
  if (!analytics) return;
  logEvent(analytics, 'team_member_invited', {
    project_id: projectId,
    role,
  });
};

export const trackTeamMemberRemoved = (projectId: string) => {
  if (!analytics) return;
  logEvent(analytics, 'team_member_removed', {
    project_id: projectId,
  });
};

// AI Assistance Events
export const trackAIAssistanceUsed = (assistanceType: 'task' | 'project' | 'description' | 'content') => {
  if (!analytics) return;
  logEvent(analytics, 'ai_assistance_used', {
    assistance_type: assistanceType,
  });
};

export const trackAISuggestionApplied = (assistanceType: 'task' | 'project' | 'description' | 'content') => {
  if (!analytics) return;
  logEvent(analytics, 'ai_suggestion_applied', {
    assistance_type: assistanceType,
  });
};

// Navigation Events
export const trackPageView = (pageName: string) => {
  if (!analytics) return;
  logEvent(analytics, 'page_view', {
    page_name: pageName,
  });
};

export const trackSearch = (searchTerm: string, searchType: 'tasks' | 'documents' | 'projects') => {
  if (!analytics) return;
  logEvent(analytics, 'search', {
    search_term: searchTerm,
    search_type: searchType,
  });
};

// Sprint Events
export const trackSprintCreated = (projectId: string) => {
  if (!analytics) return;
  logEvent(analytics, 'sprint_created', {
    project_id: projectId,
  });
};

export const trackSprintStarted = (sprintId: string) => {
  if (!analytics) return;
  logEvent(analytics, 'sprint_started', {
    sprint_id: sprintId,
  });
};

export const trackSprintCompleted = (sprintId: string) => {
  if (!analytics) return;
  logEvent(analytics, 'sprint_completed', {
    sprint_id: sprintId,
  });
};

// Error Events
export const trackError = (errorType: string, errorMessage: string) => {
  if (!analytics) return;
  logEvent(analytics, 'error', {
    error_type: errorType,
    error_message: errorMessage,
  });
};

// Feature Usage Events
export const trackFeatureUsed = (featureName: string) => {
  if (!analytics) return;
  logEvent(analytics, 'feature_used', {
    feature_name: featureName,
  });
};
